import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Outlet } from 'react-router-dom';
import { AppSettingsProvider } from '../utils/AppSettingsContext';
import { LoadingProvider } from '../utils/LoadingContext';
import MasterLayoutBody from './MasterLayoutBody';

// Context used to communicate between the persistent layout and any nested
// "virtual" layouts rendered by individual pages. When a page renders its own
// `<MasterLayout>` it will update the title/header actions in the outer layout
// without mounting a new sidebar instance.
const MasterLayoutContext = createContext(null);

const MasterLayout = ({ children, pageTitle, headerActions, headerRightActions }) => {
  const context = useContext(MasterLayoutContext);
  const [title, setTitle] = useState(pageTitle);
  const [actions, setActions] = useState(headerActions);
  const [rightActions, setRightActions] = useState(headerRightActions);

  const contextSetPageTitle = context?.setPageTitle;
  const contextSetHeaderActions = context?.setHeaderActions;
  const contextSetHeaderRightActions = context?.setHeaderRightActions;

  useEffect(() => {
    if (contextSetPageTitle || contextSetHeaderActions || contextSetHeaderRightActions) {
      if (pageTitle !== undefined) {
        contextSetPageTitle?.(pageTitle);
      }
      if (headerActions !== undefined) {
        contextSetHeaderActions?.(headerActions);
      }
      if (headerRightActions !== undefined) {
        contextSetHeaderRightActions?.(headerRightActions);
      }
      // Cleanup by clearing page-specific state when this virtual layout unmounts.
      return () => {
        if (headerRightActions !== undefined) {
          contextSetHeaderRightActions?.(null);
        }
        if (headerActions !== undefined) {
          contextSetHeaderActions?.(null);
        }
        if (pageTitle !== undefined) {
          contextSetPageTitle?.(undefined);
        }
      };
    }
  }, [
    contextSetPageTitle,
    contextSetHeaderActions,
    contextSetHeaderRightActions,
    pageTitle,
    headerActions,
    headerRightActions,
  ]);

  if (context) {
    return <>{children}</>;
  }
  const content = children ?? <Outlet />;

  return (
    <MasterLayoutContext.Provider
      value={{
        setPageTitle: setTitle,
        setHeaderActions: setActions,
        setHeaderRightActions: setRightActions,
      }}
    >
      <AppSettingsProvider>
        <LoadingProvider>
          <MasterLayoutBody
            pageTitle={title}
            headerActions={actions}
            headerRightActions={rightActions}
          >
            {content}
          </MasterLayoutBody>
        </LoadingProvider>
      </AppSettingsProvider>
    </MasterLayoutContext.Provider>
  );
};

export default MasterLayout;
